import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { P, usePermissions } from '@/features/permissions';
import { type GeneratedDocumentOut, useDeleteDocument } from '../api/documents-api';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';

export function DeleteDocumentButton({
  document,
  onDeleted,
  menu = false,
}: {
  document: GeneratedDocumentOut;
  onDeleted?: () => void;
  menu?: boolean;
}) {
  const { can } = usePermissions();
  const remove = useDeleteDocument();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  if (!can(P.DELETE_DOCUMENT) || document.signed_version_id || !['draft', 'failed'].includes(document.status))
    return null;
  async function confirm() {
    if (remove.isPending) return;
    setError('');
    try {
      await remove.mutateAsync(document.id);
      setOpen(false);
      toast.success('Report removed from the chart');
      onDeleted?.();
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
      setError(
        response?.status === 409 && typeof response.data?.detail === 'string'
          ? response.data.detail
          : response?.status === 403
            ? 'You do not have permission to delete this report.'
            : 'Could not delete this report. Refresh the chart and try again.',
      );
    }
  }
  const show = () => {
    setError('');
    setOpen(true);
  };
  return (
    <AlertDialog
      open={open}
      onOpenChange={(value) => {
        if (!remove.isPending) setOpen(value);
      }}>
      {menu ? (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            show();
          }}>
          <Trash2 />
          Delete report
        </DropdownMenuItem>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            show();
          }}>
          <Trash2 />
          Delete report
        </Button>
      )}
      <AlertDialogContent onClick={(event) => event.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {document.title || document.template?.name || 'this report'}?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the unfinished report from the chart. The source encounter and recordings remain. Signed,
            finalized, archived, and processing reports are protected.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p role="alert">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={remove.isPending} onClick={() => void confirm()}>
            {remove.isPending ? 'Deleting…' : 'Delete report'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
